import React, { useEffect, useState } from 'react';

import { ApolloError } from 'apollo-client';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ICartEntry, IServerOrder } from '@rbi-ctg/menu';
import Footer from 'components/footer';
import { SkeletonThemeType } from 'components/layout/skeleton-theme/types';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import useUserInSameRegion from 'hooks/use-user-in-same-region';
import { TimsRewardsPoints } from 'pages/account/tims-rewards/shared-onboarding-modal-components/tims-rewards-points';
import { UserDetails } from 'state/auth/hooks/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOffersContext } from 'state/offers';
import { OrderStatus, useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import useRewardDiscount from 'state/order/hooks/use-reward-discount';
import { IPaymentMethod } from 'state/payment/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { isCatering as isCateringOrder } from 'utils/service-mode';

import CartPageContainer from './cart-page-container';
import { CartRewardDropdown } from './cart-reward-dropdown';
import Content from './content';
import { CATERING_FORM_FIELDS } from './details/types';
import { Payment } from './payment';
import { CartWrap, PaymentArea, StyledDetails } from './styled';
import { MobileCartUpsellLocation } from './types';
import { Upsell } from './upsell';
import YourCart from './your-cart';
import { RewardsPointsContainer } from './your-cart/styled';

const StyledUpsell = styled(Upsell)`
  grid-area: upsell;
`;

const StyledPayment = styled(Payment)`
  grid-area: payment;
`;

interface ICartContent {
  checkoutPaymentMethod?: IPaymentMethod | null;
  serverOrder: IServerOrder | null;
  cartEntries: ICartEntry[];
  user: UserDetails | null;
  orderStatus: OrderStatus | null;
  tryPricing: any;
  unavailableCartEntries: ICartEntry[];
  priceMutationError?: ApolloError;
  priceOrderSuccess: boolean;
  hasAddOns?: boolean;
}

const CartContent = ({
  serverOrder,
  cartEntries,
  user,
  orderStatus,
  tryPricing,
  unavailableCartEntries,
  priceMutationError,
  priceOrderSuccess,
  hasAddOns = false,
  checkoutPaymentMethod,
}: ICartContent) => {
  const orderCtx = useOrderContext();
  const { loyaltyDetails } = useLoyaltyActiveCard();
  const { isDelivery } = useServiceModeContext();
  const [areDeliveryDetailsValid, setAreDeliveryDetailsValid] = useState(
    isDelivery ? Boolean(orderCtx.orderPhoneNumber) : true
  );
  const [cartDetailsErrorFields, setCartDetailsErrorFields] = useState<Array<CATERING_FORM_FIELDS>>(
    []
  );

  const upsellEnabled = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_UPSELL_ITEMS_2);
  const mobileCartUpsellLocation = useFlag(LaunchDarklyFlag.MOBILE_CART_UPSELL_LOCATION);
  const showFooter = useFlag(LaunchDarklyFlag.ENABLE_FOOTER_CART_PAGE);
  const enableTHRewardsTierToggleUI = useFlag(LaunchDarklyFlag.ENABLE_TH_REWARD_TIER_TOGGLE_UI);
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const orderServiceMode = serverOrder?.cart?.serviceMode;
  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const isCatering = orderServiceMode && isCateringOrder(orderServiceMode);
  const { isInSameRegion } = useUserInSameRegion();
  const offersCtx = useOffersContext();
  const { formatMessage } = useIntl();
  const { redeemableItemsSet } = useRewardDiscount({
    serverOrder,
    cartEntries,
  });

  const { resetStore } = useStoreContext();
  const {
    enableOrderTimedFire,
    enableDeliveryOrderTimedFire,
    enableDriveThruOrderTimedFire,
    isPickupTimeAvailable,
  } = useOrderTimedFire({
    serverOrder,
    cartEntriesActiveDayParts,
  });

  const { isCartTotalNegative = false } = orderCtx?.calculateCartTotalWithDiscount?.() || {};

  const pricedAndAvailable =
    priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;

  const legacyOfferIsValid = !offersCtx.selectedOffer || !offersCtx.offerValidationErrors.length;

  const offerIsValid = legacyOfferIsValid;

  const [hasInvalidDeliveryOptions, setHasInvalidDeliveryOptions] = useState(false);

  const isPaymentDisabled = isCartTotalNegative || hasInvalidDeliveryOptions;

  useEffect(() => {
    if (!enableOrdering) {
      resetStore();
    }
  }, [enableOrdering, resetStore]);

  const shouldShowRewards = isMobile && isInSameRegion && orderServiceMode && !isCatering;

  return (
    <>
      <Helmet title={formatMessage({ id: 'cart' })} />
      <CartPageContainer>
        {shouldShowRewards && (
          <>
            {enableTHRewardsTierToggleUI && (
              <RewardsPointsContainer>
                <TimsRewardsPoints points={loyaltyDetails?.points ?? 0} />
              </RewardsPointsContainer>
            )}
            <CartRewardDropdown skeletonTheme={SkeletonThemeType.SECONDARY} />
          </>
        )}
        <Content
          isCateringOrder={!!isCatering}
          upsellEnabled={upsellEnabled}
          mobileCartUpsellLocation={mobileCartUpsellLocation}
        >
          <StyledDetails
            enableDeliveryOrderTimedFire={enableDeliveryOrderTimedFire}
            enableDriveThruOrderTimedFire={enableDriveThruOrderTimedFire}
            enableOrderTimedFire={enableOrderTimedFire}
            onTimeSelectorChange={orderCtx.setFireOrderIn}
            onDeliveryStatusChange={setHasInvalidDeliveryOptions}
            timeSelectorValue={orderCtx.fireOrderIn}
            isPickupTimeAvailable={isPickupTimeAvailable}
            serverOrder={serverOrder}
            serviceMode={orderCtx.serviceMode}
            user={user}
            setAreDeliveryDetailsValid={setAreDeliveryDetailsValid}
            setCartDetailsErrorFields={setCartDetailsErrorFields}
          />

          {/* rendering in two distinct places for a11y flow */}
          {upsellEnabled && mobileCartUpsellLocation === MobileCartUpsellLocation.AboveYourCart && (
            <StyledUpsell hasBorderAtCheckout />
          )}

          <CartWrap>
            <YourCart
              checkoutPaymentMethod={checkoutPaymentMethod}
              redeemableItemsSet={redeemableItemsSet}
              serverOrder={serverOrder}
              showAddExtrasBtn={hasAddOns}
              pricedAndAvailable={pricedAndAvailable}
            />
          </CartWrap>

          {/* rendering in two distinct places for a11y flow */}
          {upsellEnabled &&
            mobileCartUpsellLocation === MobileCartUpsellLocation.AbovePaymentDetails && (
              <StyledUpsell hasBorderAtCheckout />
            )}
          <PaymentArea>
            <StyledPayment
              disabled={isPaymentDisabled}
              enableOrderTimedFire={enableOrderTimedFire}
              offerIsValid={offerIsValid}
              onPriceOrder={tryPricing}
              order={orderCtx}
              orderStatus={orderStatus}
              pricedAndAvailable={pricedAndAvailable}
              serverOrder={serverOrder}
              areDeliveryDetailsValid={areDeliveryDetailsValid}
              cartDetailsErrorFields={cartDetailsErrorFields}
            />
          </PaymentArea>
        </Content>
        {showFooter && <PageFooter />}
      </CartPageContainer>
    </>
  );
};

export default React.memo(CartContent);

const PageFooter = styled(Footer)`
  margin-top: auto;
`;
